import { Schema } from "effect"
import { GraphCommand, OptionEffect } from "../commands/graph"
import {
  Actor,
  ContentMeta,
  EntityRef,
  EventRef,
  Evidence,
  PermissionOptions,
  RecordMeta,
  Reversibility,
  Risk,
  Tier,
} from "../common"
import { WorkRequestDraft } from "../coordination"
import {
  AgentId,
  ArtifactId,
  ChangesetId,
  CommentId,
  IntegrationId,
  ItemId,
  Markdown,
  MemoryItemId,
  Millis,
  ProposalId,
  RunId,
  Seq,
  Sha256,
  ShortRef,
  Slug,
  UserId,
} from "../ids"
import { Requires } from "../placement"
import { Monitor, WakeReason } from "./agentlife"
import { MemoryDraft, MemoryScope } from "./knowledge"
import { Capability } from "./org"

// #region proposal
/**
 * Decision 0093: the agent that applied a person's chat answer to a card (`resolve_card`). The event's actor is the
 * person who decided; this names the agent and the turn that carried the answer.
 */
export const RelayedBy = Schema.Struct({
  agentId: AgentId,
  runId: RunId,
  /** Decision 0142: the person's words came from this teammate agent's team chat message (quoted in the note). */
  evidence: Schema.optionalKey(Schema.Struct({ agentId: AgentId, messageId: Schema.String })),
})
export type RelayedBy = typeof RelayedBy.Type

export const QuestionOption = Schema.Struct({
  key: Schema.String,
  label: Schema.String,
  detail: Schema.NullOr(Schema.String),
  /** Typed effects, re-decided against current state when chosen, with the deciding human as actor. */
  effects: Schema.Array(OptionEffect),
  /**
   * Does picking it release the items the question `blocks` (decision 0041)? Absent = `key === "approve"`. An answer
   * that does not release (and carries no effects) pauses the gated items as the person who answered.
   */
  releases: Schema.optionalKey(Schema.Boolean),
})
export type QuestionOption = typeof QuestionOption.Type

/** Why a system process or agent needs a person. System escalations only offer flow effects (retry, reassign, cancel…). */
export const EscalationCause = Schema.Literals([
  "auth",
  "repeated_failure",
  "non_retryable",
  "review_rounds_exhausted",
  "stall",
  "loop",
  "spec_conflict",
  "runner_unavailable",
  "unreported",
  /** Wave C (decision 0071): the agent's timed turns for the day reached its cap (`timed:<agentId>:<day>` card). */
  "timed_cap",
])

/**
 * Wave O1 (decision 0061, decide first): an open question on a plan card. Lea answers each one with her recommendation;
 * the person approves with it ("Use your recommendations") or with their own answer (`approve.answers`). On apply the
 * answers are written into the container's description under "## Decided before start".
 */
export const PlanQuestion = Schema.Struct({
  key: Slug,
  question: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
  recommendation: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(600)),
  why: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(400))),
})
export type PlanQuestion = typeof PlanQuestion.Type

/** One ledger, five kinds. Every surface (ghost nodes, Needs-you, chat cards, push) renders the same record by id. */
export const ProposalBody = Schema.TaggedUnion({
  /** Ghost nodes. Also covers hire agent, new area, cancelling a top-level item, KPI target changes, reverts and undos. */
  plan_change: {
    changesetId: ChangesetId,
    rationale: Markdown,
    changes: Schema.NonEmptyArray(GraphCommand), // carry real, client-minted ids: ghosts become real in place
    baseSeq: Seq,
    revertOf: Schema.NullOr(Schema.Int), // plan version this restores
    undoOf: Schema.NullOr(ChangesetId),
    /** Wave O1 (decision 0061): questions only the person can answer. Optional keys: stored bodies hash the same. */
    questions: Schema.optionalKey(Schema.Array(PlanQuestion).check(Schema.isMaxLength(8))),
    /** The item whose description receives the answers (may be created by `changes`). */
    containerId: Schema.optionalKey(ItemId),
  },
  /** To a person: up to 8 typed options, optional free text, a deadline. Escalations are questions with a cause. */
  question: {
    prompt: Markdown,
    subject: Schema.NullOr(EntityRef),
    options: Schema.Array(QuestionOption).check(Schema.isMaxLength(8)),
    allowFreeText: Schema.Boolean,
    defaultOnExpiry: Schema.NullOr(Schema.String), // option key; null = expires as "no answer"
    answersAsk: Schema.NullOr(CommentId), // an agent escalating an ask it can't answer
    escalation: Schema.NullOr(
      Schema.Struct({
        cause: EscalationCause,
        detail: Markdown,
        /** Wave B2 (decision 0057): the run that stopped, when the escalation is about one. Old escalations: absent. */
        runId: Schema.optionalKey(RunId),
        /** Wave A: the chain card. The agent wakes it held back (at most 20); Continue delivers them. */
        held: Schema.optionalKey(
          Schema.Array(Schema.Struct({ agentId: AgentId, reason: WakeReason, cause: EventRef })).check(
            Schema.isMaxLength(20),
          ),
        ),
      }),
    ),
  },
  /** Post, publish, submit, charge. Approval binds the exact arguments and the pinned tool schema. Phase 2. */
  outward_action: {
    itemId: ItemId, // a `publish` item
    integrationId: IntegrationId,
    tool: Schema.String,
    args: Schema.Json,
    argsHash: Sha256,
    toolSchemaHash: Sha256,
    preview: Schema.Struct({
      summary: Schema.String,
      recipient: Schema.NullOr(Schema.String),
      amount: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Markdown),
      diffArtifactId: Schema.NullOr(ArtifactId),
    }),
    executor: Schema.NullOr(Requires), // stdio integrations run on a machine (asc-mcp → the Mac)
  },
  /** A CLI permission prompt in a run (permission.request). Decided by the run's principal only. */
  permission: {
    runId: RunId,
    requestId: Schema.String,
    tool: Schema.String,
    title: Schema.String,
    inputPreview: Schema.String, // redacted on the runner first
    inputHash: Sha256, // the decision binds to it
    /** Runner 0.6.1 (decision 0053): Codex's "remember" choices this prompt offers. Absent = Allow / Deny only. */
    options: Schema.optionalKey(PermissionOptions),
    /**
     * Wave E (decision 0063, spec §4.4): the machine the prompt came from, stamped by the Project DO when the card opens
     * (whose machine, Just me or The team). The card's recipient reads it, so decide, reactions, the headline and the
     * web agree. Absent = unknown (cards from before E; the run's principal stands in).
     */
    machine: Schema.optionalKey(
      Schema.Struct({
        ownerId: UserId,
        serves: Schema.Literals(["owner", "team"]),
        /** Decision 0138: the workspace's Platform Runner, the team's: its project admins stand in for an owner. */
        platform: Schema.optionalKey(Schema.Literal(true)),
      }),
    ),
  },
  /** Memory create/revise/archive/promote. Applied in the scope owner's DO (via outbox for user/workspace scope). */
  memory: {
    op: Schema.Literals(["create", "revise", "archive", "promote"]),
    itemId: MemoryItemId,
    draft: Schema.NullOr(MemoryDraft),
    fromScope: Schema.NullOr(MemoryScope), // promote
    evidence: Schema.Array(Evidence),
    sourceRunId: Schema.NullOr(RunId),
    /** Decision 0146: a create that supersedes this claim (it is kept, marked superseded, when the card applies). */
    supersedes: Schema.optionalKey(MemoryItemId),
  },
  /**
   * Wave C (decision 0071): an agent's `propose_monitor` card, the full monitor as it would be set (`ownerId` = the
   * card's recipient, `trigger: cron` in the project's zone). Accept sends `SetMonitor` as the person who decides;
   * Edit first opens the monitor sheet. Agents never set a timer without a person.
   */
  monitor: { monitor: Monitor },
  project_request: { draft: WorkRequestDraft },
})
export type ProposalBody = typeof ProposalBody.Type

/**
 * Decision 0144 (experiment 11, 0136 §3): the proposing agent's recommended answer and its reason, shown above the
 * answers on every surface (card, brief, bell, push). `option`: a question's option key, or `approve` / `reject` for
 * every other card. Optional on the card: cards from before 0144 and Minga's own cards have none.
 */
export const CardRecommendation = Schema.Struct({
  option: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64)),
  reason: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(400)),
})
export type CardRecommendation = typeof CardRecommendation.Type

/** Decision 0144: at most this many characters in a card's `ask` (the question a person answers). */
export const CARD_ASK_MAX = 200

/** Who decides. Agents never decide their own proposals, nor any outward, permission, policy, grant or tainted one. */
export const Approver = Schema.TaggedUnion({
  /** A specific person (workspace owners/admins may also decide). */
  human: { userId: UserId },
  /** The project lead: low-risk untainted memory, and adopting a Mayor suggestion (re-filed under its own policy). */
  lead: { agentId: AgentId, escalateTo: UserId },
  /**
   * Decision 0102: an agent's card addressed to the agent above it first (its area lead, else the project lead). It
   * decides within its own authority, answers, or escalates one level up (`EscalateProposal`); `escalateTo` is the
   * person it reaches at the top of the chain. Not in that person's Needs you while an agent holds it.
   */
  agent: { agentId: AgentId, escalateTo: UserId },
})

/** Decision 0102: one step up the chain: who passed the card on, when, to whom, and their note (the recommendation). */
export const CardEscalation = Schema.Struct({
  by: AgentId,
  at: Millis,
  to: Approver,
  note: Schema.String,
})
export type CardEscalation = typeof CardEscalation.Type

/**
 * Wave B2 (decision 0057): why this card goes to a person instead of applying, apart from the proposer's reasoning.
 * Set by core when it files a plan change; absent on cards from before (their `reasoning` still ends with
 * "\n\nNeeds approval: …") and on questions, permissions, reviews and escalations (they are asks by nature).
 */
export const AskedRule = Schema.Literals([
  "cross_owner", // the change touches another person's agent (K3)
  "agent_level", // an agent's autonomy is set by a person (decision 0048 §3)
  "person_item", // a person's item (G7)
  "person_spec", // a person wrote the item's description
  "brief", // the brief is the owner's (decision 0049)
  "ask_first", // the agent works at Ask first
  "frozen_checks", // the done-when checks are frozen
  "policy", // the agent's policy says propose for this capability
  "policy_limit", // over the policy's limit (e.g. maxActive)
  "no_policy", // no rule allows it (default propose)
  "asked", // the agent chose to ask (propose_plan, mode propose_all)
  "depends", // depends on another change that needs approval
])
export type AskedRule = typeof AskedRule.Type
export const AskedReason = Schema.Struct({
  rule: AskedRule,
  capability: Schema.optionalKey(Capability), // policy, policy_limit, no_policy
  subject: Schema.optionalKey(EntityRef), // person_item, person_spec, frozen_checks, cross_owner (the agent)
  userId: Schema.optionalKey(UserId), // whose item or agent it is
})
export type AskedReason = typeof AskedReason.Type

export const ProposalState = Schema.Literals([
  "open",
  "revision_requested", // back to the proposer; ReviseProposal re-opens it with revision + 1
  "approved", // outward_action only: approved, waiting for its publish run
  "applied", // plan/memory applied, permission delivered, outward executed
  "apply_failed", // re-decide failed against current state (stale); proposer woken
  "rejected",
  "answered", // question
  "expired", // = denied, or the question's default option was applied
  "withdrawn",
  "adopted", // the lead re-filed a Mayor suggestion as its own command
])

export const Proposal = Schema.Struct({
  ...RecordMeta, // ownerId = the person the proposal is for (routing)
  ...ContentMeta,
  id: ProposalId,
  ref: ShortRef, // P-58
  body: ProposalBody,
  revision: Schema.Int,
  /** sha256 over the canonical encoded body. DecideProposal must echo revision + contentHash (no TOCTOU). */
  contentHash: Sha256,
  proposedBy: Actor,
  reasoning: Markdown, // shown on the ghost node and the inbox card
  oneLine: Schema.String, // inbox headline: "why now"
  /**
   * Decision 0144 (0136 §3): the question a person answers, as the proposing agent wrote it ("Start sync PR 1 now,
   * before your library test?"). Absent on older cards and Minga's own: surfaces derive one (core `cardAsk`).
   */
  ask: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(CARD_ASK_MAX))),
  /** Decision 0144: the proposer's recommended answer and why (above the answers). Absent: none given. */
  recommendation: Schema.optionalKey(CardRecommendation),
  /**
   * Decision 0144 (0136 §2): the decision's own ref (D-n), minted when a person or an agent decides the card (approve,
   * reject, answer). Absent while open, and on cards decided before 0144.
   */
  decisionRef: Schema.optionalKey(ShortRef),
  /** Wave B2: why a person decides (the rule); `reasoning` is only the proposer's why. Old cards: absent. */
  asked: Schema.optionalKey(Schema.Array(AskedReason).check(Schema.isMaxLength(12))),
  risk: Risk,
  reversibility: Reversibility,
  tier: Tier,
  blocks: Schema.Array(EntityRef), // what waits on it: the items it names and everything under them (`waiting_decision`, G2)
  tainted: Schema.Boolean, // most tainted input of the proposing run; tainted ⇒ approver is a human
  approver: Approver,
  decideBy: Schema.NullOr(Millis), // soft deadline shown in the inbox
  expiresAt: Schema.NullOr(Millis), // hard; expiry = denied
  state: ProposalState,
  perChange: Schema.Array(Schema.Literals(["pending", "accepted", "rejected"])), // plan_change only, index-aligned
  resolution: Schema.NullOr(
    Schema.Struct({
      by: Actor,
      at: Millis,
      optionKey: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Schema.String),
      note: Schema.NullOr(Schema.String),
      changesetId: Schema.NullOr(ChangesetId), // the changeset that applied it
      /** Wave E (decision 0063): the card's recipient when someone else decided it ("decided by Kate for Becky"). */
      for: Schema.optionalKey(UserId),
      /** Decision 0116: the agent that applied the person's chat answer ("Jannik (in chat), applied by Lea"). */
      relayedBy: Schema.optionalKey(RelayedBy),
    }),
  ),
  /** Meaning-based dedupe: kind + subject + intent. A duplicate open proposal is rejected with a pointer. */
  intentKey: Schema.String,
  /** Decision 0102: the card's way up the chain, oldest first (absent: never escalated). */
  escalations: Schema.optionalKey(Schema.Array(CardEscalation).check(Schema.isMaxLength(10))),
})
export type Proposal = typeof Proposal.Type
// #endregion proposal
